import Image from 'next/image'
import clsx from 'clsx'

/* blocks */
import Link from '@/atoms/Link/Link'

/* utils */
import { getPostExcerpt } from '@/utils/functions/blog'
import { dataLayerPostClickEvent } from '@/utils/functions/dataLayerEvents'
import { mmddyyyyDateFormat } from '@/utils/functions/utils'

interface Props extends BlogImageProps {
  className?: string
  categories: { id: string | number; name: string }[]
  alt?: string
  title: string
  description: string
  date: string
  slug: string
  authorName?: string
  readTime?: string
  isNew?: boolean
  bgColor?: string
  isAside?: boolean
}

interface BlogImageProps {
  isFirstFold?: boolean
  image?: {
    url: string
    altText: string
    width: number
    height: number
  } | null
  type: BlogCardType
}

type BlogCardType = 'featured' | 'default' | 'minimal' | 'search'

function BlogCard({ bgColor = '#070425', type = 'default', ...props }: Props) {
  const handleBlogCardClick = () => {
    dataLayerPostClickEvent({
      title: props.title,
      type: 'blog',
      url: props.slug
    })
  }

  const blogCategory = props.categories?.[0].name
  const description = getPostExcerpt(props.description)

  return (
    <>
      {type === 'minimal' ? (
        <Link
          href={clsx(props.isAside ? `/blog/${props.slug}/` : props.slug)}
          className="hover:text-blue-50"
        >
          <div className={clsx(props.isAside ? 'py-3' : 'py-4')}>
            {blogCategory !== 'Uncategorized' ? (
              <div className="pb-4">
                <span
                  className={clsx(
                    'text-caption lg:text-base text-white | px-1 lg:px-2 py-1 rounded-sm',
                    props.isAside ? 'bg-blue-web-60' : 'bg-blue-90'
                  )}
                >
                  {blogCategory}
                </span>
              </div>
            ) : // spacer with equivalent height taken by category to keep headings at same height
            !props.isAside ? (
              <div className="h-6 mb-4" />
            ) : null}

            <div
              className={clsx(
                'font-heading font-semibold hover:text-blue-50 | line-clamp-2',
                props.isAside
                  ? 'text-xl text-blue-90 | mb-3'
                  : 'text-base md:text-2xl | mb-4 lg:h-20'
              )}
            >
              {props.title}
            </div>
            {description ? (
              <p
                className={clsx(
                  'font-body text-gray-70 | line-clamp-2 | mb-3',
                  props.isAside ? ' text-sm' : ' text-base'
                )}
                dangerouslySetInnerHTML={{
                  __html: description
                }}
              />
            ) : (
              // spacer with equivalent height taken by category to keep headings at same height
              <div className="h-10 mb-3" />
            )}
            {!props.isAside ? (
              <div className="flex justify-between text-gray-60 text-caption md:text-xs | mb-3">
                <div className="flex gap-3">
                  <p>{props.authorName}</p>
                  <p>{props.readTime || ''} mins read</p>
                </div>
                <div>{mmddyyyyDateFormat(props.date)}</div>
              </div>
            ) : null}
            {props.isAside ? (
              <hr className="border-gray-60" />
            ) : (
              <hr className="border-gray-30" />
            )}
          </div>
        </Link>
      ) : null}

      {type === 'featured' ? (
        <Link href={props.slug} onClick={handleBlogCardClick}>
          <div
            className="grid grid-cols-1 lg:grid-cols-12 | rounded-2xl p-5 lg:p-14 text-white"
            style={{ background: bgColor }}
          >
            <div className="col-span-7 | order-2 lg:order-1">
              <div className="mb-3 hidden lg:block">
                {props.isNew ? (
                  <span className="font-body uppercase text-base text-black | bg-blue-30 rounded-md | py-1 px-2">
                    New
                  </span>
                ) : null}
              </div>
              <div>
                <h2 className="text-2xl lg:text-3xl font-semibold | line-clamp-2 | max-w-col-5 mb-9">
                  {props.title}
                </h2>

                <div
                  className="text-base lg:text-lg font-body | line-clamp-2 | max-w-col-5 mb-10 lg:mb-22"
                  dangerouslySetInnerHTML={{
                    __html: getPostExcerpt(props.description)
                  }}
                />
              </div>
              <div className="flex flex-wrap justify-start items-center gap-3 lg:gap-6 | font-body">
                {blogCategory !== 'Uncategorized' ? (
                  <p
                    className={clsx(
                      'text-caption lg:text-base text-black bg-yellow-40 px-1 lg:px-2 py-1 rounded-sm'
                    )}
                  >
                    {blogCategory}
                  </p>
                ) : (
                  // spacer with equivalent height taken by category to keep headings at same height
                  <div className="h-5 mb-2" />
                )}
                <div
                  className={clsx('text-caption lg:text-base tracking-wider')}
                >
                  <span className="text-white">
                    {mmddyyyyDateFormat(props.date)}
                  </span>
                </div>
                <div
                  className={clsx('text-caption lg:text-base tracking-wider')}
                >
                  <p>{props.readTime || ''} mins read</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center lg:col-start-8 lg:col-end-13 | order-1 lg:order-2">
              {props.isNew ? (
                <div className="mb-3 lg:hidden">
                  <span className="font-body uppercase text-xs text-black | bg-blue-30 rounded-md | py-1 px-2">
                    New
                  </span>
                </div>
              ) : null}

              {props.image ? (
                <div className="mb-5 lg:mb-0 mx-auto">
                  <Image
                    src={props.image.url}
                    alt={props.image.altText}
                    width={props.image.width}
                    height={props.image.height}
                    priority={props.isFirstFold}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </Link>
      ) : null}

      {type === 'default' || type === 'search' ? (
        <Link
          href={props.slug}
          onClick={handleBlogCardClick}
          className="hover:shadow-md transition-all duration-200 ease-in"
        >
          <div
            className={clsx(
              'flex flex-col | text-black | border border-gray-30 hover:border-blue-50 transition-all duration-200 ease-in rounded-md | h-full',
              props.isAside ? 'bg-white | mb-6' : ''
            )}
          >
            {props.image ? (
              <div>
                <Image
                  src={props.image.url}
                  alt={props.image.altText}
                  width={props.image.width}
                  height={props.image.height}
                  priority={props.isFirstFold}
                  className="rounded-tl-md rounded-tr-md"
                />
              </div>
            ) : (
              <div className="h-48 mb-2" />
            )}
            <div className="font-heading | p-6">
              {blogCategory !== 'Uncategorized' ? (
                <div className="flex items-center gap-3 | mb-4">
                  <p className="font-body text-small bg-black text-white | rounded-sm inline | px-2 py-1">
                    {blogCategory}
                  </p>
                  <div
                    className={clsx('text-caption lg:text-small font-semibold')}
                  >
                    <p>{props.readTime || ''} mins read</p>
                  </div>
                </div>
              ) : (
                // spacer with equivalent height taken by category to keep headings at same height
                <div className="h-7 mb-3" />
              )}
              <h2 className="text-base lg:text-xl font-semibold | line-clamp-2 hover:text-blue-50 | mb-1 lg:mb-2 lg:h-12">
                {props.title}
              </h2>
              {description ? (
                <div
                  className="text-sm text-gray-70 line-clamp-2"
                  dangerouslySetInnerHTML={{
                    __html: getPostExcerpt(description)
                  }}
                />
              ) : (
                <div className="h-9" />
              )}
            </div>
          </div>
        </Link>
      ) : null}
    </>
  )
}

export default BlogCard
